import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, Settings } from "lucide-react";
import { WordPressIntegration } from "@/types/integration";
import { pb } from "@/lib/pocketbase";

interface WordPressSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  brandId: string;
  integration: WordPressIntegration;
}

export function WordPressSettingsDialog({
  open,
  onOpenChange,
  onSuccess,
  brandId,
  integration,
}: WordPressSettingsDialogProps) {
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    autoPublish: integration.settings?.autoPublish ?? true,
    defaultAuthor: integration.settings?.defaultAuthor || "",
    defaultTags: integration.settings?.defaultTags || "",
    defaultStatus: integration.settings?.defaultStatus || "publish",
  });

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${pb.baseURL}/api/v1/wordpress/update-settings`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: pb.authStore.token,
          },
          body: JSON.stringify({
            brandId,
            autoPublish: settings.autoPublish,
            defaultAuthor: settings.defaultAuthor,
            defaultTags: settings.defaultTags,
            defaultStatus: settings.defaultStatus,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || "Failed to update WordPress settings",
        );
      }

      toast.success("WordPress settings updated successfully");
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error("Error updating WordPress settings:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to update WordPress settings",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>WordPress Settings</DialogTitle>
          <DialogDescription>
            Configure your WordPress publishing preferences
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {/* Auto-publish toggle */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="auto-publish">Auto-publish articles</Label>
              <p className="text-sm text-muted-foreground">
                Automatically publish articles to WordPress when they're created
              </p>
            </div>
            <Switch
              id="auto-publish"
              checked={settings.autoPublish}
              onCheckedChange={(checked) =>
                setSettings((prev) => ({
                  ...prev,
                  autoPublish: checked,
                }))
              }
            />
          </div>

          {/* Default author */}
          <div className="space-y-2">
            <Label htmlFor="default-author">Default Author (Optional)</Label>
            <Input
              id="default-author"
              placeholder="e.g., Admin"
              value={settings.defaultAuthor}
              onChange={(e) =>
                setSettings((prev) => ({
                  ...prev,
                  defaultAuthor: e.target.value,
                }))
              }
            />
            <p className="text-sm text-muted-foreground">
              Author name to use when publishing articles
            </p>
          </div>

          {/* Default tags */}
          <div className="space-y-2">
            <Label htmlFor="default-tags">Default Tags (Optional)</Label>
            <Input
              id="default-tags"
              placeholder="e.g., blog, news, updates"
              value={settings.defaultTags}
              onChange={(e) =>
                setSettings((prev) => ({
                  ...prev,
                  defaultTags: e.target.value,
                }))
              }
            />
            <p className="text-sm text-muted-foreground">
              Comma-separated tags to add to published articles
            </p>
          </div>

          {/* Default status */}
          <div className="space-y-2">
            <Label htmlFor="default-status">Default Status</Label>
            <select
              id="default-status"
              value={settings.defaultStatus}
              onChange={(e) =>
                setSettings((prev) => ({
                  ...prev,
                  defaultStatus: e.target.value,
                }))
              }
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="publish">Publish</option>
              <option value="draft">Draft</option>
              <option value="pending">Pending Review</option>
            </select>
            <p className="text-sm text-muted-foreground">
              Default publication status for new articles
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button onClick={handleSaveSettings} disabled={loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Settings
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface WordPressSettingsButtonProps {
  brandId: string;
  integration: WordPressIntegration;
  onSuccess: () => void;
}

export function WordPressSettingsButton({
  brandId,
  integration,
  onSuccess,
}: WordPressSettingsButtonProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="flex items-center gap-2"
      >
        <Settings className="h-4 w-4" />
        Settings
      </Button>
      <WordPressSettingsDialog
        open={open}
        onOpenChange={setOpen}
        onSuccess={onSuccess}
        brandId={brandId}
        integration={integration}
      />
    </>
  );
}
